import React, { useState, useEffect, useRef } from 'react';
import { Clip, EmotionMood, StemSet } from '../../types';
import { separateAudioStems } from '../../services/audioSeparationService';
import { Sparkles, Music, Mic2, Play, Pause, Volume2, CheckCircle2, ArrowRight, X } from 'lucide-react';

interface SoulSeparatorModalProps {
  clip: Clip;
  mood: EmotionMood;
  isOpen: boolean;
  onClose: () => void;
  onStemsReady: (stems: StemSet) => void;
}

export const SoulSeparatorModal: React.FC<SoulSeparatorModalProps> = ({
  clip,
  mood,
  isOpen,
  onClose,
  onStemsReady
}) => {
  const [progress, setProgress] = useState(10);
  const [stageText, setStageText] = useState('Initializing Demucs Vocal Isolation Pipeline...');
  const [stems, setStems] = useState<StemSet | null>(null);
  const [isDone, setIsDone] = useState(false);

  // Audition state
  const [playingStem, setPlayingStem] = useState<'vocals' | 'bgm' | null>(null);
  const [vocalsVolume, setVocalsVolume] = useState(0.8);
  const [bgmVolume, setBgmVolume] = useState(0.8);

  const vocalsAudioRef = useRef<HTMLAudioElement | null>(null);
  const bgmAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setProgress(10);
      setIsDone(false);
      setStems(null);
      setPlayingStem(null);
      return;
    }

    let isMounted = true;

    async function runPipeline() {
      try {
        const result = await separateAudioStems(
          clip.id,
          clip.videoUrl,
          clip.trimStart,
          clip.trimEnd,
          mood,
          (prog, text) => {
            if (isMounted) {
              setProgress(prog);
              setStageText(text);
            }
          }
        );

        if (isMounted) {
          setStems(result);
          setIsDone(true);
        }
      } catch (err) {
        console.error('Stem separation error:', err);
      }
    }

    runPipeline();

    return () => {
      isMounted = false;
      if (vocalsAudioRef.current) vocalsAudioRef.current.pause();
      if (bgmAudioRef.current) bgmAudioRef.current.pause();
    };
  }, [isOpen, clip, mood]);

  if (!isOpen) return null;

  const toggleStemPlayback = (stemType: 'vocals' | 'bgm') => {
    if (stemType === 'vocals') {
      if (playingStem === 'vocals') {
        vocalsAudioRef.current?.pause();
        setPlayingStem(null);
      } else {
        bgmAudioRef.current?.pause();
        vocalsAudioRef.current?.play();
        setPlayingStem('vocals');
      }
    } else {
      if (playingStem === 'bgm') {
        bgmAudioRef.current?.pause();
        setPlayingStem(null);
      } else {
        vocalsAudioRef.current?.pause();
        bgmAudioRef.current?.play();
        setPlayingStem('bgm');
      }
    }
  };

  const handleLaunchStudio = () => {
    if (stems) {
      if (vocalsAudioRef.current) vocalsAudioRef.current.pause();
      if (bgmAudioRef.current) bgmAudioRef.current.pause();
      onStemsReady(stems);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container" style={{ maxWidth: '640px' }}>
        {/* Hidden Audio Elements for Auditioning */}
        {stems && (
          <>
            <audio
              ref={vocalsAudioRef}
              src={stems.vocalsUrl}
              onEnded={() => setPlayingStem(null)}
              loop
            />
            <audio
              ref={bgmAudioRef}
              src={stems.bgmUrl}
              onEnded={() => setPlayingStem(null)}
              loop
            />
          </>
        )}

        {/* Modal Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <div className="badge badge-glow-purple" style={{ marginBottom: '0.4rem' }}>
              <Sparkles size={13} />
              <span>Soul Separator AI</span>
            </div>
            <h2 style={{ fontSize: '1.5rem' }}>Splitting Dialogue & Backing Score</h2>
            <p style={{ fontSize: '0.85rem' }}>Target: {clip.title} ({clip.trimStart}s - {clip.trimEnd}s)</p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Dynamic Progress Indicator */}
        <div style={{ marginBottom: '1.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: '6px' }}>
            <span style={{ color: '#ffffff', fontWeight: 600 }}>{stageText}</span>
            <span style={{ color: 'var(--accent-purple)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
              {progress}%
            </span>
          </div>

          <div
            style={{
              width: '100%',
              height: '8px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 'var(--radius-full)',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: 'var(--accent-gradient)',
                borderRadius: 'var(--radius-full)',
                transition: 'width 300ms ease-out',
                boxShadow: '0 0 12px rgba(139, 92, 246, 0.6)'
              }}
            />
          </div>
        </div>

        {/* Stems Audition Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
          {/* Vocals Stem Card */}
          <div
            className="glass-card"
            style={{
              borderColor: playingStem === 'vocals' ? 'var(--accent-coral)' : 'var(--border-glass)',
              background: playingStem === 'vocals' ? 'rgba(255, 107, 107, 0.12)' : undefined
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'rgba(255, 107, 107, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--accent-coral)'
                  }}
                >
                  <Mic2 size={16} />
                </div>
                <div>
                  <h4 style={{ fontSize: '0.95rem', margin: 0 }}>vocals.mp3</h4>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Original Dialogue</span>
                </div>
              </div>
              {isDone && <CheckCircle2 size={16} color="var(--accent-emerald)" />}
            </div>

            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Isolated actor dialogue track. Used in studio as an optional guide or muted during dub recording.
            </p>

            <button
              onClick={() => toggleStemPlayback('vocals')}
              disabled={!isDone}
              className="btn-secondary"
              style={{
                width: '100%',
                padding: '0.45rem 0.8rem',
                fontSize: '0.82rem',
                opacity: isDone ? 1 : 0.4
              }}
            >
              {playingStem === 'vocals' ? <Pause size={14} /> : <Play size={14} />}
              <span>{playingStem === 'vocals' ? 'Pause Dialogue' : 'Audition Vocals'}</span>
            </button>
          </div>

          {/* Background Music Stem Card */}
          <div
            className="glass-card"
            style={{
              borderColor: playingStem === 'bgm' ? 'var(--accent-cyan)' : 'var(--border-glass)',
              background: playingStem === 'bgm' ? 'rgba(6, 182, 212, 0.12)' : undefined
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'rgba(6, 182, 212, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--accent-cyan)'
                  }}
                >
                  <Music size={16} />
                </div>
                <div>
                  <h4 style={{ fontSize: '0.95rem', margin: 0 }}>background_music.mp3</h4>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Score + SFX</span>
                </div>
              </div>
              {isDone && <CheckCircle2 size={16} color="var(--accent-emerald)" />}
            </div>

            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Clean backing orchestration, environment sound, and impact Foley ready for your fresh vocal take.
            </p>

            <button
              onClick={() => toggleStemPlayback('bgm')}
              disabled={!isDone}
              className="btn-secondary"
              style={{
                width: '100%',
                padding: '0.45rem 0.8rem',
                fontSize: '0.82rem',
                opacity: isDone ? 1 : 0.4
              }}
            >
              {playingStem === 'bgm' ? <Pause size={14} /> : <Play size={14} />}
              <span>{playingStem === 'bgm' ? 'Pause BGM' : 'Audition Backing'}</span>
            </button>
          </div>
        </div>

        {/* Cloudflare Storage Destination Notice */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid var(--border-glass)',
            borderRadius: 'var(--radius-md)',
            padding: '0.75rem 1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.78rem',
            color: 'var(--text-muted)'
          }}
        >
          <span>Cloudflare R2 Storage:</span>
          <span style={{ fontFamily: 'var(--font-mono)', color: '#ffffff' }}>
            soulsync-media/stems/{clip.id}/
          </span>
        </div>

        {/* Action Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleLaunchStudio}
            disabled={!isDone}
            className="btn-primary"
            style={{ opacity: isDone ? 1 : 0.5 }}
          >
            <span>Enter Dubbing Studio</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
